import { goalbookUrls, toolkitUrls } from '@/util/links.js'
import { ContentListResponse, LibraryFolder, LibraryFolders, LibraryFolderResponse, ContributorUserInfo, ContributorRoleChangeResponse, DeleteResourceResponse, LibraryStrategy, LibraryGoal, FolderUsersResponse, CollectionNewContributionsResponse, UserNewContributionsResponse } from '@/library/library_types'
import { getHandleRequest, getCSRFHeaders } from '@/authentication/request_handler'
import { sendHTTPRequest } from "@/util/http.ts";

export async function getFolderResources(uuid: string, params = {}): Promise<LibraryFolderResponse> {
  const handleRequest = getHandleRequest()
  if (handleRequest) {
    return handleRequest({ requestFn: getFolderResourcesRequest }, uuid, params) as Promise<LibraryFolderResponse>
  }
  return Promise.reject(new Error('Request handler not initialized'))
}

export async function getFolderResourcesRequest(folderUuid: string, params = {}): Promise<Response> {
  const urlParams = new URLSearchParams(params)
  const url = `${toolkitUrls.apiBases.collections}/${folderUuid}?${urlParams.toString()}`
  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: getCSRFHeaders()
  })
  return response
}

export async function getSharedFolder(shareToken: string, params = {}): Promise<LibraryFolderResponse> {
  const urlParams = new URLSearchParams(params)
  const url = `${toolkitUrls.apiBases.collections}/share/${shareToken}?${urlParams.toString()}`
  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: getCSRFHeaders()
  })
  const responseJSON = await response.json()
  if (response.status >= 200 && response.status < 300) {
    return responseJSON
  }
  throw new Error('Failed to get shared folder', { cause: responseJSON })
}

export async function deleteFolderResource(folderUuid: string, contentUuid: string, resourceType: string): Promise<DeleteResourceResponse> {
  const handleRequest = getHandleRequest();
  if (handleRequest) {
    return handleRequest({ requestFn: deleteFolderResourceRequest }, folderUuid, contentUuid, resourceType) as Promise<DeleteResourceResponse>
  }
  return Promise.reject(new Error('Request handler not initialized'))
}

export async function deleteFolderResourceRequest(folderUuid: string, contentUuid: string, resourceType: string): Promise<Response> {
  const url = `${toolkitUrls.apiBases.collections}/${folderUuid}/${contentUuid}/${resourceType}`
  return await fetch(url, {
    method: 'DELETE',
    credentials: 'include',
    headers: getCSRFHeaders()
  })
}


export async function getRecentFolders(userUuid: string): Promise<LibraryFolder[]> {
  const url = `${toolkitUrls.apiBases.library}/${userUuid}/recent_students`
  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: getCSRFHeaders()
  })
  const responseJSON = await response.json()
  if (response.status >= 200 && response.status < 300) {
    return responseJSON
  }
  throw new Error('Failed to get recent folders', { cause: responseJSON })
}

export async function getFolders(userId: string): Promise<LibraryFolders> {
  const handleRequest = getHandleRequest();
  if (handleRequest) {
    return handleRequest({ requestFn: getFoldersRequest }, userId) as Promise<LibraryFolders>
  }
  return Promise.reject(new Error('Request handler not initialized'))
}

export async function getFoldersRequest(userUuid: string): Promise<Response> {
  const url = `${toolkitUrls.apiBases.collections}/customresources/${userUuid}/students`
  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: getCSRFHeaders()
  })
  return response
}

export async function getArchivedFolders(userId: string): Promise<LibraryFolders> {
  const handleRequest = getHandleRequest();
  if (handleRequest) {
    return handleRequest({ requestFn: getArchivedFoldersRequest }, userId) as Promise<LibraryFolders>
  }
  return Promise.reject(new Error('Request handler not initialized'))
}

export async function getArchivedFoldersRequest(userUuid: string): Promise<Response> {
  const url = `${toolkitUrls.apiBases.collections}/customresources/${userUuid}/students/archived`
  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: getCSRFHeaders()
  })
  return response
}

export async function addFolder(folderName: string, userUuid: string): Promise<LibraryFolder> {
  const handleRequest = getHandleRequest();
  if (handleRequest) {
    return handleRequest({ requestFn: addFolderRequest }, folderName, userUuid) as Promise<LibraryFolder>
  }
  return Promise.reject(new Error('Request handler not initialized'))
}

export async function addFolderRequest(folderName: string, userUuid: string): Promise<Response> {
    const url = `${toolkitUrls.apiBases.library}/${userUuid}/student`
    const response = await fetch(url, {
      method: 'POST',
      headers: getCSRFHeaders(),
      body: JSON.stringify({
        name: folderName
      }),
      credentials: 'include'
    })

    return response
}

export async function updateCollectionContent({ collectionUuid, contentUuid, url, contentTitle, userUuid }: {
  collectionUuid: string, contentUuid: string, url?: string, contentTitle?: string, userUuid: string
}): Promise<unknown | null> {
  const handleRequest = getHandleRequest()
  if (handleRequest) {
    return handleRequest({ requestFn: updateCollectionContentRequest }, { collectionUuid, contentUuid, url, contentTitle, userUuid }) as Promise<unknown | null>
  }
  return Promise.reject(new Error('Request handler not initialized'))
}

export async function updateCollectionContentRequest({ collectionUuid, contentUuid, url, contentTitle, userUuid }: {
  collectionUuid: string, contentUuid: string, url?: string, contentTitle?: string, userUuid: string
}): Promise<Response> {
  const endpointUrl = `${goalbookUrls.toolkit}/api/library/attachment/${contentUuid}`
  const payload: Record<string, unknown> = {
    collection_uuid: collectionUuid,
    user_uuid: userUuid
  }
  if (url !== undefined) {
    payload.url = url
  }
  if (contentTitle !== undefined) {
    payload.content_title = contentTitle
  }
  const response = await fetch(endpointUrl, {
    method: 'PUT',
    credentials: 'include',
    headers: getCSRFHeaders(),
    body: JSON.stringify(payload)
  })
  return response
}

export async function updateFolder({ userUuid, folderUuid, updateParams }: {
  userUuid: string, folderUuid: string, updateParams: {[key: string]: unknown}
}): Promise<LibraryFolder> {
  const handleRequest = getHandleRequest();
  if (handleRequest) {
    return handleRequest({ requestFn: updateFolderRequest }, userUuid, folderUuid, updateParams) as Promise<LibraryFolder>
  }
  return Promise.reject(new Error('Request handler not initialized'))
}

export async function updateFolderRequest(userUuid: string, folderUuid: string, updateParams: {[key: string]: unknown}): Promise<Response> {
    const url = `${toolkitUrls.apiBases.library}/${userUuid}/student/${folderUuid}`
    const response = await fetch(url, {
      method: 'PUT',
      credentials: 'include',
      headers: getCSRFHeaders(),
      body: JSON.stringify(updateParams),
    })
    return response
}

export async function deleteFolderRequest(userUuid: string, folderUuid: string): Promise<Response> {
    const url = `${toolkitUrls.apiBases.library}/${userUuid}/student/${folderUuid}`
    const response = await fetch(url, {
      method: 'DELETE',
      headers: getCSRFHeaders(),
      credentials: 'include'
    })
    return response
}

export async function deleteFolder({ userUuid, folderUuid }: { userUuid: string, folderUuid: string }): Promise<unknown | null> {
   const handleRequest = getHandleRequest();
    if (handleRequest) {
      return handleRequest({ requestFn: deleteFolderRequest }, userUuid, folderUuid) as Promise<unknown>
    }
    return Promise.reject(new Error('Request handler not initialized'))
  }

export async function getAllResourcesForContentTypes(userUuid: string, contentTypes: string[], include_shared: boolean = false): Promise<ContentListResponse> {
  const contentTypesString = encodeURIComponent(contentTypes.join(','))
  const queryParams = new URLSearchParams({ include_shared: `${include_shared}` })
  try {
    const response = await sendHTTPRequest({
      url: `${toolkitUrls.apiBases.library}/${userUuid}/allresources/${contentTypesString}?${queryParams.toString()}`,
      method: 'GET',
      includeCredentials: true
    })
    return await response.json() as ContentListResponse
  } catch (e) {
    throw new Error('Failed to get all resources for content types', { cause: e })
  }
}

export async function getRecentlyViewedContent(userUuid: string, contentType: string, limit?: number): Promise<ContentListResponse> {
  let url = `${toolkitUrls.apiBases.library}/${userUuid}/recently_viewed/${contentType}`
  if (limit) {
    url += `?limit=${limit}`
  }
  try {
    const response = await sendHTTPRequest({
      url: url,
      method: 'GET',
      includeCredentials: true
    })
    return await response.json() as ContentListResponse
  } catch (e) {
    throw new Error('Failed to get recent content', { cause: e })
  }
}

export async function addCollectionContent({ collectionUuid, contentType, contentUuid, contributor, url, contentTitle }: {
  collectionUuid: string, contentType: string, contentUuid: string, contributor: string, url?: string, contentTitle?: string
}): Promise<unknown | null> {
  const handleRequest = getHandleRequest()
  if (handleRequest) {
    return handleRequest({requestFn: addCollectionContentRequest}, { collectionUuid, contentType, contentUuid, contributor, url, contentTitle }) as Promise<unknown | null>
  }
  return Promise.reject(new Error('Request handler not initialized'));
}

export async function addCollectionContentRequest({ collectionUuid, contentType, contentUuid, contributor, url, contentTitle }: {
  collectionUuid: string, contentType: string, contentUuid: string, contributor: string, url?: string, contentTitle?: string
}): Promise<Response> {
    const baseUrl = 'api/collections'
    const endpointUrl = `${goalbookUrls.toolkit}/${baseUrl}/content/add`
    const payload: Record<string, unknown> = {
      collection_uuid: collectionUuid,
      content_type: contentType,
      content_uuid: contentUuid,
      contributor: contributor
    }
    if (url !== undefined) {
      payload.url = url
    }
    if (contentTitle !== undefined) {
      payload.content_title = contentTitle
    }
    const response = await fetch(endpointUrl, {
      method: 'POST',
      credentials: 'include',
      headers: getCSRFHeaders(),
      body: JSON.stringify(payload)
    })
    return response
}

export async function copyCollection(folderUuid: string, nameOfCopy: string): Promise<LibraryFolder> {
  const handleRequest = getHandleRequest();
  if (handleRequest) {
    return handleRequest({ requestFn: copyCollectionRequest }, folderUuid, nameOfCopy) as Promise<LibraryFolder>;
  }
  return Promise.reject(new Error('Request handler not initialized'));
}

export async function copyCollectionRequest(folderUuid: string, nameOfCopy: string): Promise<Response> {
  const baseUrl = 'api/collections'
  const url = `${goalbookUrls.toolkit}/${baseUrl}/${folderUuid}/copy`
  const response = await fetch(url, {
    method: 'POST',
    headers: getCSRFHeaders(),
    credentials: 'include',
    body: JSON.stringify({
      name_of_copy: nameOfCopy
    })
  })
  return response
}


async function addGoalToFolderRequest(folder: { uuid: string, name: string }, goalPayload: Record<string, any>): Promise<Response> {
  const url = `${toolkitUrls.apiBases.library}/goal`
  const payload = {
    student_uuid: folder.uuid,
    student_name: folder.name,
    for_student: true,
    ...goalPayload
  }
  const response = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: getCSRFHeaders(),
    body: JSON.stringify(payload)
  })

  return response
}

export async function addGoalToFolder(folder: { uuid: string, name: string }, goalPayload: Record<string, any>): Promise<LibraryGoal> {
  const handleRequest = getHandleRequest()
  if (handleRequest) {
    return handleRequest({requestFn: addGoalToFolderRequest}, folder, goalPayload) as Promise<LibraryGoal>
  }
  return Promise.reject(new Error('Request handler not initialized'))
}

export async function addStrategyToFolder({
  userUuid,
  strategyUuid,
  folderUuid,
  folderName,
  notes,
}: {
  userUuid: string,
  strategyUuid: string,
  folderUuid: string,
  folderName: string,
  notes?: string,
}): Promise<LibraryStrategy> {
  const handleRequest = getHandleRequest()
  if (handleRequest) {
    return handleRequest({requestFn: addStrategyToFolderRequest}, { userUuid, strategyUuid, folderUuid, folderName, notes }) as Promise<LibraryStrategy>
  }
  return Promise.reject(new Error('Request handler not initialized'))
}

export async function addStrategyToFolderRequest({
  userUuid,
  strategyUuid,
  folderUuid,
  folderName,
  notes,
}: {
  userUuid: string,
  strategyUuid: string,
  folderUuid: string,
  folderName: string,
  notes?: string,
}): Promise<Response> {
  const url = `${toolkitUrls.apiBases.library}/strategy`
  const payload = {
    user_uuid: userUuid,
    strategy_uuid: strategyUuid,
    student_uuid: folderUuid,
    student_name: folderName,
    notes: notes
  }

  const response = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: getCSRFHeaders(),
    body: JSON.stringify(payload)
  })

  return response
}

export async function getCollectionUsers(collectionUuid: string): Promise<FolderUsersResponse> {
  const handleRequest = getHandleRequest();
  if (handleRequest) {
    return handleRequest({ requestFn: getCollectionUsersRequest }, collectionUuid) as Promise<FolderUsersResponse>
  }
  return Promise.reject(new Error('Request handler not initialized'))
}

export async function getCollectionUsersRequest(collectionUuid: string): Promise<Response> {
  const url = `${toolkitUrls.api}/collection/${collectionUuid}/users`
  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: getCSRFHeaders()
  })
  return response
}

export async function queryUsersInDomain(domainUuuid: string, queryString: string): Promise<ContributorUserInfo[]> {
  try {
    const response = await sendHTTPRequest(
      {
        url: `${goalbookUrls.pathwaysApi}/domain/${domainUuuid}/users?query=${encodeURIComponent(queryString)}`,
        method: 'GET',
        includeCredentials: true
      })
    return await response.json()
  } catch (e) {
    throw new Error('Failed to users in domain', { cause: e })
  }
}
export async function grantContributors(collectionUuid: string, targetUsers: string[], emailMessage?: string): Promise<ContributorRoleChangeResponse | null> {
  const handleRequest = getHandleRequest();
  if (handleRequest) {
    return handleRequest({ requestFn: grantContributorsRequest }, { collectionUuid, targetUsers, emailMessage }) as Promise<ContributorRoleChangeResponse>
  }
  return Promise.reject(new Error('Request handler not initialized'))
}

export async function grantContributorsRequest({ collectionUuid, targetUsers, emailMessage = '' }: {
  collectionUuid: string, targetUsers: string[], emailMessage?: string
}): Promise<Response> {
  const body: { target_users: string[], send_email?: boolean, email_message?: string } = {
    target_users: targetUsers,
    send_email: true
  }
  if (emailMessage && emailMessage.length > 0) {
    body.email_message = emailMessage
  }
  const response = await fetch(`${toolkitUrls.api}/collection/${collectionUuid}/contributors`, {
    method: 'POST',
    headers: getCSRFHeaders(),
    body: JSON.stringify(body),
    credentials: 'include'
  })
  return response
}

export async function revokeContributors(collectionUuid: string, targetUsers: string[]): Promise<ContributorRoleChangeResponse | null> {
  const handleRequest = getHandleRequest();
  if (handleRequest) {
    return handleRequest({ requestFn: revokeContributorsRequest }, { collectionUuid, targetUsers }) as Promise<ContributorRoleChangeResponse>;
  }
  return Promise.reject(new Error('Request handler not initialized'));
}

export async function revokeContributorsRequest({ collectionUuid, targetUsers }: {
  collectionUuid: string, targetUsers: string[]
}): Promise<Response> {
    const response = await fetch(`${toolkitUrls.api}/collection/${collectionUuid}/contributors/revoke`, {
      method: 'POST',
      headers: getCSRFHeaders(),
      body: JSON.stringify({
        target_users: targetUsers,
      }),
      credentials: 'include'
    })
    return response
}

export async function transferOwnership(collectionUuid: string, newOwner: string): Promise<{ message: string }> {
  const handleRequest = getHandleRequest();
  if (handleRequest) {
    return handleRequest({ requestFn: transferOwnershipRequest }, { collectionUuid, newOwner }) as Promise<{ message: string }>;
  }
  return Promise.reject(new Error('Request handler not initialized'));
}
  
export async function transferOwnershipRequest({ collectionUuid, newOwner }: { collectionUuid: string, newOwner: string }): Promise<Response> {
  const response = await fetch(`${toolkitUrls.api}/collection/${collectionUuid}/transfer`, {
    method: 'POST',
    headers: getCSRFHeaders(),
    body: JSON.stringify({
      new_owner: newOwner,
    }),
    credentials: 'include'
  })
  return response
}

export async function getCollectionNewContributions(collectionUuid: string): Promise<CollectionNewContributionsResponse> {
  const handleRequest = getHandleRequest();
  if (handleRequest) {
    return handleRequest({ requestFn: getCollectionNewContributionsRequest }, collectionUuid) as Promise<CollectionNewContributionsResponse>
  }
  return Promise.reject(new Error('Request handler not initialized'))
}

export async function getCollectionNewContributionsRequest(collectionUuid: string): Promise<Response> {
  const url = `${toolkitUrls.api}/collection/${collectionUuid}/new_contributions`
  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: getCSRFHeaders()
  })
  return response
}

export async function getUserNewContributionCounts(userUuid: string): Promise<UserNewContributionsResponse> {
  const handleRequest = getHandleRequest();
  if (handleRequest) {
    return handleRequest({ requestFn: getUserNewContributionCountsRequest }, userUuid) as Promise<UserNewContributionsResponse>
  }
  return Promise.reject(new Error('Request handler not initialized'))
}

export async function getUserNewContributionCountsRequest(userUuid: string): Promise<Response> {
  const url = `${toolkitUrls.api}/library/${userUuid}/new_contribution_counts`
  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: getCSRFHeaders()
  })
  return response
}